/**
 * [PM-SG 2026-08-11] TIMING — every figure the competition holds, on one screen.
 *
 * Sixteen columns across a television is about 113px each, and a figure like 19,069.89 nearly
 * fills that. It would technically fit and be miserable to read, which is why F1's own timing
 * screen never presents a flat row of columns — it GROUPS. So does this: six groups, each with
 * its own header and a hairline between them. The eye then navigates six things, not sixteen.
 *
 * Three decisions do most of the work of making a dense table readable from across a room:
 *
 *   ZEROS ARE DIMMED. Recurring upsell is 0.00 for all ten PMs and disputes won is 0.00 for all
 *   ten — a fifth of this table is currently shouting nothing. At 30% they stop competing and
 *   the eye lands on what actually moved.
 *
 *   THE TWO GATE COLUMNS ARE TINTED. Retention % decides who is classified and Efficiency is
 *   what they are ranked on. Everything else is evidence; those two are the verdict, so they
 *   read as answer columns rather than as two of sixteen.
 *
 *   COLOUR MEANS SOMETHING. Green is money kept, red is money lost, purple is the leader, amber
 *   is not-classified — the same language every other page on this board speaks.
 *
 * The running order is identical to every other page: classified by efficiency, then the pit
 * lane by retention. On an unattended board, consistency is worth more than sortability.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { LINE, TEXT, RED, AMBER, PURPLE, GREEN, EASE } from './chrome';
import { LeaderData, LeaderRow, fmtNum, fmtPctValue } from './raceModel';

/** Each row deals itself in from the left, in finishing order. */
const rowIn = keyframes`
  from { opacity: 0; transform: translateX(-23.04px); }
`;

const Wrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* Overwritten from the real names on mount and on every resize — see fitName(). The 17% is a
     FALLBACK for the first paint only. */
  --tname: 17%;
`;

const Strip = styled.header`
  flex: 0 0 auto;
  display: flex;
  align-items: baseline;
  gap: 28px;
  padding: 0 15.36px 9.72px;
  border-bottom: 1px solid ${LINE};
  position: relative;

  &::after {
    content: '';
    position: absolute; left: 15.36px; bottom: -1px;
    width: 192px; height: 2px; background: ${RED};
  }

  h1 { font-size: 31px; font-weight: 900; letter-spacing: 0.14em; margin: 0; }
  .month { font-size: 21px; font-weight: 700; letter-spacing: 0.18em; opacity: 0.7; }
  .count { margin-left: auto; font-size: 17px; font-weight: 700; letter-spacing: 0.2em; opacity: 0.75; }
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

/**
 * Column widths are proportional, not fixed, so the table fills whatever panel it lands on
 * rather than leaving a gutter on an ultrawide or clipping on a laptop.
 */
const Table = styled.table`
  width: 100%;
  height: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
  table-layout: fixed;

  /**
   * ⚠ FIFTEEN COLUMNS DO NOT SHRINK INDEFINITELY. Proportional widths keep the table filling a
   * wall, but on a laptop or a phone they carry on dividing until REFUND + DISP is 40px wide and
   * every figure is truncated — a table nobody can read at any size.
   *
   * Below the width where the columns still hold a figure, the table stops shrinking and the
   * wrapper's overflow takes over: the numbers stay legible and the page scrolls sideways to
   * reach them. Scrolling to see a figure is a far smaller cost than not being able to read one.
   */
  /* [PM-SG FILL 2026-08-13] Was a 1180px min-width with a viewport media query lifting it above
     1181. Both are pointless now: the canvas is never narrower than 1600, so the floor could never
     bind, and the media query was reading the viewport while the table sits on the canvas. The
     sideways-scroll fallback it protected cannot be reached, so the floor simply goes. */
  min-width: 0;

  th, td { padding: 0 8.64px; }

  /* group headers */
  thead .grp th {
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.24em;
    text-align: center;
    padding-bottom: 3.24px;
    color: ${TEXT};
    opacity: 0.75;
  }
  /* column headers */
  thead .col th {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-align: right;
    opacity: 0.75;
    padding-bottom: 7.56px;
    border-bottom: 1px solid ${LINE};
    white-space: nowrap;
  }
  thead .col th.l { text-align: left; }

  /* A group boundary is a hairline, not a heavy rule — it should organise, not divide. */
  .sep { border-left: 1px solid rgba(255, 255, 255, 0.09); }

  /**
   * [2026-08-12] EVERY COLUMN IS RULED, not just the group edges.
   *
   * Inside a group the figures ran together — REFUND + DISP and DISP WON sat in one open field
   * with nothing between them, so telling which column a 0.00 belonged to meant tracking up to
   * the header and back down. On a fifteen-column table read from a distance that is the whole
   * ball game.
   *
   * Two weights, because the six groups must still read as groups: a faint rule between columns
   * inside a group, a brighter one at each group edge. The lines run the full height of the
   * table — header, body and the totals row — or they stop meaning "column" halfway down.
   *
   * ⚠ Specificity: the adjacent-sibling rule below is (0,1,2) and would out-rank a bare .sep at
   * (0,1,0), so the group edge is re-stated with the class ON the element to win it back.
   */
  thead .col th + th,
  tbody td + td,
  tfoot td + td { border-left: 1px solid rgba(255, 255, 255, 0.05); }

  thead .col th.sep,
  tbody td.sep,
  tfoot td.sep { border-left-color: rgba(255, 255, 255, 0.17); }

  tbody tr { border-bottom: 1px solid rgba(255, 255, 255, 0.045); }
  tbody tr:nth-child(even) { background: rgba(255, 255, 255, 0.016); }

  /**
   * [2026-08-12] THE FIELD ARRIVES ROW BY ROW instead of the table simply being there.
   *
   * A static table of fifteen columns lands as a wall of figures and the eye has nowhere to
   * start. Dealt out top to bottom it is read in the order it means — P1 first — and the page
   * announces itself the way every other page on this board does.
   *
   * ⚠ No entry token needed: the views are conditionally rendered, so TIMING genuinely unmounts
   * when the circuit moves on and remounts when it comes back. A mount-time animation therefore
   * replays on every visit, while a thirty-second poll — which only re-renders the same nodes —
   * cannot re-trigger it. That is the behaviour wanted in both cases.
   */
  /* ⚠ The STEP is what makes this read as one row at a time, and it has to be longer than the
     row's own animation or the rows overlap into a single wave. 340ms apart with a 300ms arrival
     means each row is fully in place before the next one starts, and ten of them take about
     three and a half seconds — deliberately unhurried, because the point is to be watched. */
  tbody tr {
    animation: ${rowIn} 300ms ${EASE} backwards;
    animation-delay: calc(var(--r, 0) * 340ms);
  }
  /* The totals land once the field they total has finished arriving. */
  tfoot tr {
    animation: ${rowIn} 300ms ${EASE} backwards;
    animation-delay: calc(var(--rows, 10) * 340ms + 200ms);
  }
  @media (prefers-reduced-motion: reduce) {
    tbody tr, tfoot tr { animation: none; }
  }

  tbody td {
    text-align: right;
    font-size: 22px;
    font-weight: 700;
    white-space: nowrap;
  }
  /* Zeros stop competing for attention. */
  tbody td.zero { opacity: 0.55; font-weight: 400; }
  tbody td.gain { color: ${GREEN}; }
  tbody td.loss { color: ${RED}; }

  /* the verdict columns */
  tbody td.key, thead .col th.key { background: rgba(255, 255, 255, 0.045); }
  tbody td.key { font-weight: 900; }

  tfoot td {
    text-align: right;
    font-size: 19px;
    font-weight: 900;
    padding-top: 8.64px;
    border-top: 2px solid ${LINE};
    white-space: nowrap;
  }
  tfoot td.l { text-align: left; letter-spacing: 0.2em; opacity: 0.75; font-size: 15px; }
  /* [PM-SG 2026-08-13] Same green as tbody td.gain, so a total reads as the same KIND of figure as
     the column it sums. Only DISP WON asks for this today; the rule is written generally so any
     other total can opt in by adding the class rather than by inventing a second green. */
  tfoot td.gain { color: ${GREEN}; }
`;

/* [PM-SG-LB 2026-09-18] `$nc` is gone with the gate — there is no unclassified PM to mark
   amber or to dash the livery bar for. Every row is a runner. */
const Who = styled.td<{ $lead: boolean }>`
  text-align: left !important;
  display: flex;
  align-items: center;
  gap: 11.52px;
  height: 100%;

  .pos {
    flex: 0 0 auto;
    min-width: 2.4em;
    font-size: 22px;
    font-weight: 900;
    color: ${(p) => (p.$lead ? PURPLE : TEXT)};
  }
  .bar {
    flex: 0 0 auto;
    width: 5.38px;
    height: 1.5em;
    background: var(--c);
  }
  .nm {
    font-size: 22px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** A money cell: dimmed when it is zero, tinted when the column carries a direction. */
/**
 * A money cell.
 *
 * [2026-09-18, user] IT CARRIES THE $. Every column on this table is an amount, and a bare
 * 47,420 beside a 181.4% gives a reader nothing to tell the units apart by at ten feet.
 *
 * ⚠ dp DEFAULTS TO 0, not 2. Half these columns passed dp={0} and half took the default, so the
 * same row printed "47,420" next to "15,492.00" - two different formats for the same kind of
 * figure. On a wall board the cents are noise; the whole-dollar columns had already made that
 * call and the rest were just never brought into line.
 */
const Money: React.FC<{
  v: number | null; dp?: number; tone?: 'gain' | 'loss'; sep?: boolean; keyCol?: boolean;
}> = ({ v, dp = 0, tone, sep, keyCol }) => {
  const n = Number(v ?? 0);
  const zero = !n;
  const cls = [
    zero ? 'zero' : tone || '',
    sep ? 'sep' : '',
    keyCol ? 'key' : '',
  ].filter(Boolean).join(' ');
  return <td className={cls}>${fmtNum(n, dp)}</td>;
};

const TimingView: React.FC<{ data: LeaderData }> = ({ data }) => {
  const rows = data.runners;
  /* [PM-SG TOP10 2026-09-21] The table lists the top ten only, but FIELD TOTAL is still the WHOLE
     field: it is what the benchmark is the average of, so "placed = benchmark x head count" keeps
     holding on the wall, and it matches the pit wall's PLACED INVENTORY / TOTAL IN BANK. */
  const field = data.field;
  const sum = (pick: (r: LeaderRow) => number) =>
    field.reduce((n, r) => n + (Number(pick(r)) || 0), 0);

  /* The field's own efficiency. Guards its denominator: an empty month must print a dash,
     not NaN%. */
  const fieldEff = data.effectiveInventory > 0 && field.length
    ? (sum((r) => r.totalInBank) / (data.effectiveInventory * field.length)) * 100
    : null;

  const wrapRef = useRef<HTMLDivElement>(null);

  /**
   * [2026-08-12] THE DRIVER COLUMN IS MEASURED, NOT GUESSED.
   *
   * At a flat 17% the longest names were ellipsised — "Shreyansh Gu…", "Shobhit Srivas…" — and a
   * board that abbreviates the people on it is worse than one that gives up a little width. A
   * percentage cannot know that this month's roster contains "Shreyansh Gupta", and next month's
   * roster is somebody else's names entirely.
   *
   * It measures the OVERFLOW of the rendered names rather than the text on its own, which is what
   * makes it exact: whatever else shares that cell — the position, the livery bar, the completion
   * chip, the padding — is already accounted for in the number the browser reports.
   *
   * The variable is cleared before each pass so the column can SHRINK as well as grow; without
   * that a wide roster in August would leave the column wide for a short roster in September.
   * The cap keeps this from eating the table on a narrow window: a name is worth width, but not
   * at the price of every figure beside it.
   */
  const fitName = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    wrap.style.removeProperty('--tname');
    const cell = wrap.querySelector<HTMLElement>('tbody tr td:first-child');
    const names = wrap.querySelectorAll<HTMLElement>('tbody tr td:first-child .nm');
    if (!cell || !names.length) return;

    let over = 0;
    names.forEach((n) => { over = Math.max(over, n.scrollWidth - n.clientWidth); });
    if (over <= 0) return; // nothing is being truncated — the fallback width is already enough

    const table = cell.closest('table');
    const cap = table ? table.clientWidth * 0.32 : Infinity;
    wrap.style.setProperty('--tname', `${Math.ceil(Math.min(cell.offsetWidth + over + 2, cap))}px`);
  }, []);

  /* Re-run on the data as well as on resize: the type is sized in vw, so the same column is the
     wrong width at another viewport, and a new month brings new names. */
  useLayoutEffect(() => { fitName(); }, [fitName, rows]);

  useEffect(() => {
    const ro = new ResizeObserver(() => fitName());
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [fitName]);

  return (
    <Wrap ref={wrapRef}>
      <Strip>
        <h1>TIMING</h1>
        {/* [2026-09-18, user] The running totals START in August, so the header names the
            start rather than the end. Printing only "SEPTEMBER 2026" over a cumulative table
            reads as a September-only figure, which is what every column here is not. */}
        <span className="month">STARTING {data.startMonth}</span>
        <span className="count">
          {rows.length < data.total ? `TOP ${rows.length} OF ${data.total}` : data.total} PROJECT MANAGERS
          {' '}· BENCHMARK ${fmtNum(data.effectiveInventory, 0)}
        </span>
      </Strip>

      <Scroll>
        <Table>
          <colgroup>
            <col style={{ width: 'var(--tname)' }} />
            <col span={2} style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
            <col span={3} style={{ width: '8%' }} />
            {/* [2026-08-12] REFUND + DISP is the longest header on the table and at 7% its text
                was running straight through the column rule to its right. It is given the width
                its own label needs; DISP WON next to it is five characters shorter and does not
                need the same. ⚠ These percentages already total more than 100 — the browser
                normalises them — so widening one column quietly narrows every other. */}
            <col style={{ width: '9.4%' }} />
            <col style={{ width: '8%' }} />
            <col span={2} style={{ width: '8%' }} />
          </colgroup>

          <thead>
            {/* [PM-SG-LB 2026-09-18] Twelve columns, all CUMULATIVE since August. The old
                THE MONTH / TO DATE split is gone with the billing-date cutoff — there is only
                one set of figures now, so there is only one group of money columns. */}
            <tr className="grp">
              <th />
              <th colSpan={2} className="sep">THE BOOK</th>
              <th className="sep">RETAINED</th>
              <th colSpan={3} className="sep">UPSELLS</th>
              {/* Split, because these two columns are opposites: REFUND + DISP is money gone and
                  DISP WON is money clawed back. One header reading LOSSES over both was calling
                  a recovery a loss. */}
              <th className="sep">LOSSES</th>
              <th className="sep">SAVED</th>
              <th colSpan={2} className="sep">RESULT</th>
            </tr>
            <tr className="col">
              <th className="l">PMs</th>
              <th className="sep">ORIGINAL PLACED</th>
              <th>EFFECTIVE INV</th>
              <th className="sep">PLACED IN BANK</th>
              <th className="sep">RECURRING</th>
              <th>ONE-TIME</th>
              <th>TOTAL UPSELL</th>
              <th className="sep">REFUND + DISP</th>
              {/* Carries the hairline too, now that it is a group of its own rather than the
                  second half of LOSSES. */}
              <th className="sep">DISP WON</th>
              <th className="sep key">TOTAL IN BANK</th>
              <th className="key">EFFICIENCY %</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((d, i) => {
              const r = d.raw;
              return (
                <tr key={d.name} style={{ '--r': i } as React.CSSProperties}>
                  <Who
                    as="td"
                    $lead={d.leader}
                    style={{ '--c': d.color } as React.CSSProperties}
                  >
                    <span className="pos">{d.pos}</span>
                    <span className="bar" />
                    <span className="nm">{d.name}</span>
                  </Who>

                  <Money v={r.originalPlacedInventory} sep />
                  {/* The same value on every row — it is the field benchmark, not a personal
                      figure — so it is dimmed to say so rather than repeated at full weight. */}
                  <td className="zero">${fmtNum(r.effectiveInventory, 0)}</td>

                  <Money v={r.placedInBank} tone="gain" sep />

                  <Money v={r.recurringUpsell} tone="gain" sep />
                  <Money v={r.oneTimeUpsell} tone="gain" />
                  <Money v={r.totalUpsell} tone="gain" />

                  <Money v={r.refundDisputeLost} tone="loss" sep />
                  <Money v={r.disputeWon} tone="gain" sep />

                  <Money v={r.totalInBank} keyCol sep />
                  {/* ⚠ efficiencyPct is ALREADY a percent — 181.37 means 181.37%. Never put it
                      through a helper that multiplies by 100 (raceModel's fmtPct does); that
                      prints 18137.00% on a wall. fmtPctValue is the one that does not. */}
                  <td className="key">{fmtPctValue(r.efficiencyPct)}</td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr style={{ '--rows': rows.length } as React.CSSProperties}>
              <td className="l">FIELD TOTAL</td>
              <td className="sep">${fmtNum(sum((r) => r.originalPlacedInventory), 0)}</td>
              {/* The identity the job asserts on every run: the placed column sums to the field
                  average times the head count. Printing it as a product rather than a total is
                  what makes that checkable from the wall. */}
              <td className="zero">× {field.length}</td>
              <td className="sep">${fmtNum(sum((r) => r.placedInBank), 0)}</td>
              <td className="sep">${fmtNum(sum((r) => r.recurringUpsell), 0)}</td>
              <td>${fmtNum(sum((r) => r.oneTimeUpsell), 0)}</td>
              <td>${fmtNum(sum((r) => r.totalUpsell), 0)}</td>
              <td className="sep">${fmtNum(sum((r) => r.refundDisputeLost), 0)}</td>
              {/* [PM-SG 2026-08-13] DISP WON is money clawed back, so the FIELD TOTAL carries the
                  same green the per-PM cells above it already carry. Green only when there is
                  something to show: a zero total stays neutral, like the zeros in the rows. */}
              <td className={`sep${sum((r) => r.disputeWon) ? ' gain' : ''}`}>
                ${fmtNum(sum((r) => r.disputeWon), 0)}
              </td>
              <td className="sep key">${fmtNum(sum((r) => r.totalInBank), 0)}</td>
              {/* The field's own efficiency — a real, meaningful number where the old footer
                  printed a dash. */}
              <td className="key">{fieldEff === null ? '—' : fmtPctValue(fieldEff)}</td>
            </tr>
          </tfoot>
        </Table>
      </Scroll>
    </Wrap>
  );
};

/**
 * [PM-SG SMOOTH 2026-08-13] Memoised. The board shell re-renders on its own state — the page
 * rotation tick, the sweep, an announcement, the diagnostic toggle, a resize — none of which
 * change this view's props. Without this, each of those re-rendered the whole view mid-animation.
 */
export default React.memo(TimingView);
